import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { cn } from "@/lib/utils";

// shadcn/ui Imports
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, PlayCircle } from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerChildren = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

export default function HeroSection({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  // Motion values for interactive 3D effect on the card
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-200, 200], [8, -8]);
  const rotateY = useTransform(x, [-200, 200], [-8, 8]);

  const handleMouseMove = (event) => {
    if (isMobile) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    x.set(0);
    y.set(0);
  };

  return (
    <section id="hero" className="relative pt-32 pb-24 md:pt-40 md:pb-32 lg:pt-44 lg:pb-40 bg-gradient-to-b from-background to-background/90 overflow-hidden">
      {/* Subtle Animated Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-50">
        <motion.div
          className="absolute rounded-full blur-3xl opacity-15"
          style={{ 
            backgroundColor: 'hsl(var(--primary))',
            width: isMobile ? '20rem' : '40rem',
            height: isMobile ? '20rem' : '40rem',
            top: isMobile ? '-10rem' : '-15rem',
            left: isMobile ? '-10rem' : '-20rem',
          }}
          animate={{
            x: [0, 20, -15, 0],
            y: [0, -15, 25, 0],
            scale: [1, 1.05, 0.95, 1]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute rounded-full blur-3xl opacity-15"
          style={{
            backgroundColor: 'hsl(var(--secondary))',
            width: isMobile ? '18rem' : '36rem',
            height: isMobile ? '18rem' : '36rem',
            bottom: isMobile ? '-8rem' : '-12rem',
            right: isMobile ? '-8rem' : '-18rem',
          }}
          animate={{
            x: [0, -25, 18, 0],
            y: [0, 20, -12, 0],
            scale: [1, 1.03, 0.97, 1]
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Content Container */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-x-12 lg:gap-x-16 items-center">
          {/* Text Content Column */}
          <div className={cn(
            "md:col-span-7 lg:col-span-6", 
            isMobile ? "text-center" : (isRTL ? "md:text-right" : "md:text-left")
          )}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerChildren}
              className="space-y-6"
            >
              <motion.div variants={fadeInUp}>
                <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-primary bg-primary/10 rounded-full">
                  {t('homepage.hero.tag', 'Next-Gen Fleet Intelligence')}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
                  {t('homepage.hero.title', 'Navigate the Future of Fleet Management')}
                </h1>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <p className="text-lg text-muted-foreground max-w-xl mx-auto md:mx-0">
                  {t('homepage.hero.subtitle', 'Unlock unparalleled efficiency and safety with our AI-powered telematics platform. Real-time insights, predictive analytics, and seamless integration.')}
                </p>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <div className={cn(
                  "flex gap-3 mt-8",
                  isMobile ? "flex-col sm:flex-row sm:justify-center" : "flex-row",
                  isRTL && !isMobile ? "md:justify-end" : "md:justify-start"
                )}>
                  <Button
                    //onClick={() => console.log("Request Demo Clicked")}
                    size="lg"
                    className="w-full sm:w-auto group"
                  >
                    {t('homepage.hero.cta.primary', 'Request a Demo')}
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>

                  <Button
                    //onClick={() => console.log("Watch Video Clicked")}
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto group"
                  >
                    <PlayCircle className="h-4 w-4 mr-2 transition-transform group-hover:scale-110" />
                    {t('homepage.hero.cta.secondary', 'Watch Overview')}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Dashboard Preview Column */}
          <div className="md:col-span-5 lg:col-span-6">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="w-full p-1 sm:p-2"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <Card className="w-full overflow-hidden border-border/20 bg-background/70 backdrop-blur-md shadow-2xl shadow-primary/10">
                <CardContent className="p-0">
                  <motion.div
                    className="rounded-lg overflow-hidden"
                    style={{
                      rotateX: rotateX,
                      rotateY: rotateY,
                      transform: isMobile ? 'none' : 'translateZ(0px)'
                    }}
                  >
                    <img
                      src="https://placehold.co/1200x800/000000/FFF?text=Dashboard+Preview"
                      alt={t('homepage.hero.dashboardPreview.alt', 'Al Saqr Platform Dashboard Preview')}
                      className="w-full h-auto object-cover"
                    />
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Optional: Scroll down indicator (minimal) */}
      {!isMobile && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        >
          <button
            onClick={() => {
              const nextSection = document.getElementById('segments');
              if (nextSection) {
                nextSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            aria-label="Scroll to next section"
            className="p-2 rounded-full hover:bg-primary/10 transition-colors"
          >
            <motion.svg
              animate={{ y: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="h-6 w-6 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </motion.svg>
          </button>
        </motion.div>
      )}
    </section>
  );
}
